// BILL-FE-02: one place that says whether the organisation accepts writes. While it is read-only
// or suspended the server answers 403 ORGANIZATION_READ_ONLY to every write but the billing
// payments and the exports; the main create/edit buttons are disabled here, with the reason,
// instead of failing after a click.
import { can } from '@batiflow/client-core';
import { useActiveMembership } from '@batiflow/client-core/react';
import { Tooltip } from '@fluentui/react-components';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { isLockedStatus } from './model';

export interface OrganizationWritable {
  /** False while the organisation is read-only or suspended. */
  writable: boolean;
  /** `read_only` or `suspended` when the organisation refuses writes, else null. */
  reason: 'read_only' | 'suspended' | null;
  /** Plain-language reason for a disabled action (null when writes are accepted). */
  explanation: string | null;
}

/**
 * Whether the active organisation accepts writes, from the status the session already carries
 * (GET /me), with the explanation to show next to a disabled action. The owner is told how to
 * unlock the office, the other members whom to ask.
 */
export function useOrganizationWritable(): OrganizationWritable {
  const { t } = useTranslation('web');
  const membership = useActiveMembership();
  const status = membership?.organizationStatus;
  if (!isLockedStatus(status)) return { writable: true, reason: null, explanation: null };
  const owner = can(membership?.permissions, 'billing.manage');
  return {
    writable: false,
    reason: status,
    explanation: t(`billing.locked.${status}.${owner ? 'owner' : 'member'}`),
  };
}

/**
 * Wraps a button that creates or edits something: while writes are refused, the explanation
 * is attached to it (tooltip and accessible description). The button itself must be given
 * `disabledFocusable={!writable}` so that keyboard users can reach it and hear the reason.
 */
export function WriteGuard({ children }: { children: ReactElement }) {
  const { writable, explanation } = useOrganizationWritable();
  if (writable || explanation === null) return children;
  return (
    <Tooltip content={explanation} relationship="description" withArrow>
      {children}
    </Tooltip>
  );
}
